// src/shared/json-table/postgrest.ts
import type { Filter, Sort, TableState } from "./types";

export type ListResponse<T> = {
  rows: T[];
  total: number; // exact count (requires Prefer: count=exact)
};

const encodeOr = (parts: string[]) => `(${parts.join(",")})`;

const toOrderParam = (sort: Sort[]) =>
  sort.length
    ? sort.map(s => `${s.id}.${s.desc ? "desc" : "asc"}`).join(",")
    : undefined;

const encodeFilterKV = (f: Filter): [string, string] => {
  // Map Filter -> PostgREST key=value (URLSearchParams will encode)
  if (f.op === "in") {
    const values = Array.isArray(f.value) ? f.value : String(f.value).split(",");
    return [f.id, `in.(${values.map(String).join(",")})`];
  }
  if (f.op === "ilike" || f.op === "like") {
    const term = String(f.value);
    const wildcard = term.includes("*") || term.includes("%") ? term : `*${term}*`;
    return [f.id, `${f.op}.${wildcard}`];
  }
  if (f.op === "is") {
    // is.null | is.true | is.false
    return [f.id, `is.${String(f.value)}`];
  }
  return [f.id, `${f.op}.${String(f.value)}`];
};

export const buildPostgrestQuery = (args: {
  baseUrl: string;
  resource: string;
  select: string;
  state: TableState;
  staticFilters?: Filter[];
  globalSearchCols?: string[];
}) => {
  const { baseUrl, resource, select, state, staticFilters, globalSearchCols } = args;
  const full = `${(baseUrl || '').replace(/\/$/, "")}/${resource}`;
  let url: URL;
  try {
    url = new URL(full);
  } catch {
    // Support relative base URLs (e.g., "/dev-mock") in the browser
    const fallbackBase = typeof window !== 'undefined' && (window as any).location?.origin
      ? (window as any).location.origin
      : 'http://localhost';
    url = new URL(full, fallbackBase);
  }

  url.searchParams.set("select", select);
  url.searchParams.set("limit", String(state.pageSize));
  url.searchParams.set("offset", String(state.pageIndex * state.pageSize));

  const order = toOrderParam(state.sort);
  if (order) url.searchParams.set("order", order);

  // Filters
  [...(staticFilters ?? []), ...state.filters].forEach((f) => {
    const [k, v] = encodeFilterKV(f);
    url.searchParams.append(k, v);
  });

  // Global search => or=(c1.ilike.*q*,c2.ilike.*q*)
  if (state.q && globalSearchCols?.length) {
    const parts = globalSearchCols.map((c) => `${c}.ilike.*${state.q}*`);
    url.searchParams.set("or", encodeOr(parts));
  }

  return url.toString();
};

export const fetchPostgrestList = async <T>(
  input: { url: string; headers?: Record<string, string> }
): Promise<ListResponse<T>> => {
  const res = await fetch(input.url, {
    headers: {
      ...(input.headers ?? {}),
      Prefer: "count=exact",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PostgREST error ${res.status}: ${text || res.statusText}`);
  }

  const rows = (await res.json()) as T[];

  // Content-Range: 0-19/123  OR */0 if empty
  const cr = res.headers.get("Content-Range");
  const total = cr && /\/(\d+)$/.test(cr)
    ? parseInt(RegExp.$1, 10)
    : rows.length;

  return { rows, total };
};